import { useRef, useState } from 'react'

import { Box, Button, Tabs } from '@factorialco/deprecated-design-system'
import type moment from 'moment'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useAddShiftPreview } from 'modules/shift_management/hooks/useAddShiftPreview'
import useCommonSelectors from 'modules/shift_management/hooks/useCommonSelectors'
import useCustomShiftForm from 'modules/shift_management/hooks/useCustomShiftForm'
import { getWeekDay } from 'modules/shift_management/hooks/useDateRangeSelection'
import useDefaultShiftForms from 'modules/shift_management/hooks/useDefaultShiftForms'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { useMutateCustomShifts } from 'modules/shift_management/hooks/useMutateCustomShifts'
import { useMutateDefaultShifts } from 'modules/shift_management/hooks/useMutateDefaultShifts'
import { useSaveShift } from 'modules/shift_management/hooks/useSaveShift'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import CommonSelectorsForm, {
  TIME_PERIODS,
} from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import { Leave, type TabsType } from 'modules/shift_management/lib/types'

import CustomShiftSection from './CustomShiftSection'
import SavedShiftsSection from './SavedShiftsSection'

interface Props {
  onClose: () => void
  date?: moment.Moment
  noSavedShifts?: boolean
  advancedOptions?: boolean
  isBackup?: boolean
  leave?: Leave
}

const AddShiftSidepanel = ({
  onClose,
  date,
  noSavedShifts,
  advancedOptions = false,
  isBackup = false,
  leave,
}: Props) => {
  const [tab, setTab] = useState<TabsType>(noSavedShifts ? 'custom-shift' : 'saved-shifts')
  const isSavedShiftsTab = tab === 'saved-shifts'
  const defaultShiftForm = useLazyRef(() => new DefaultShiftForm()).current
  const [showSavedShiftsError, setShowSavedShiftsError] = useState(false)
  const [trackSearch, setTrackSearch] = useState<string>('')
  const scrollToErrorsTimeout = useRef<ReturnType<typeof setTimeout>>()
  const { isTimeFormat24H } = useIsTimeFormat24H()
  const [{ view }] = useSchedule()
  const { isEnabled: hasSelection } = useSelectionState()

  const [hasSeenBackupShiftsCallout, setHasSeenBackupShiftsCallout] = useState<boolean>(false)

  const commonSelectorsForm = useLazyRef<CommonSelectorsForm>(
    () =>
      new CommonSelectorsForm({
        start_date: leave
          ? dateToString(leave._startOn, 'backend')
          : dateToString(view.firstDay, 'backend'),
        end_date:
          leave && leave._finishOn
            ? dateToString(leave._finishOn, 'backend')
            : dateToString(view.lastDay, 'backend'),
        apply_on_days: view.isDaily ? getWeekDay(view.startAt) : getWeekDay(view.currentDateString),
        time_period: leave ? TIME_PERIODS.DAILY : TIME_PERIODS.DOES_NOT_REPEAT,
      })
  ).current
  const {
    defaultAttributesGraphQL,
    loadingEmployees: loading,
    selectableEmployeeIds,
    weekDays,
    daysByEmployee,
  } = useCommonSelectors({ date, leave, form: commonSelectorsForm })

  const { plannedBreakForms, onAddBreak, onDeleteBreak, customShiftForm } = useCustomShiftForm({
    daysByEmployee,
    isTimeFormat24H,
  })

  const { onSubmit: onSubmitCustomShift, loading: submittingCustomShift } = useMutateCustomShifts({
    employeeSelection: commonSelectorsForm.employeeSelection,
    defaultAttributes: defaultAttributesGraphQL,
    forms: [customShiftForm],
    plannedBreakForms,
    daysByRow: daysByEmployee,
    weekDays,
    from: 'side_panel',
    advancedOptions,
    isBackup,
    leave,
  })

  const { forms: defaultShiftForms, onToggleShift } = useDefaultShiftForms()

  const { onSubmit: onSubmitDefaultShifts, loading: submittingDefaultShifts } =
    useMutateDefaultShifts({
      // If has manual selection in frontend, we build the object using daysByRow and send it to the backend.
      // The backend will handle the employee selection only if we use the EmployeeSelectorV2.
      employeeSelection: hasSelection ? undefined : commonSelectorsForm.employeeSelection,
      defaultAttributes: defaultAttributesGraphQL,
      daysByRow: daysByEmployee,
      weekDays,
      from: 'side_panel',
      withSearch: trackSearch,
      advancedOptions,
      isBackup,
      leave,
    })

  const { onSubmit: onSubmitSaveShift, submitting: submittingSavedShift } = useSaveShift({
    customShiftForm,
    defaultShiftForm,
    plannedBreakForms,
    // Skip creating breaks because we create them from the default shift in the backend async.
    onSuccess: (defaultShiftId) => onSubmitCustomShift({ defaultShiftId }),
  })

  const { cleanPreview } = useAddShiftPreview({
    daysByRow: daysByEmployee,
    customShiftForm,
    defaultShiftForms,
    plannedBreakForms,
    selectedTab: tab,
    isBackup,
  })

  const handleErrors = (): boolean => {
    const isCommonValid = commonSelectorsForm.validate()
    if (isSavedShiftsTab) {
      const hasSelected = defaultShiftForms.some((form) => form.isSelected)
      hasSelected ? setShowSavedShiftsError(false) : setShowSavedShiftsError(true)

      return isCommonValid && hasSelected
    } else {
      const isCustomValid = customShiftForm.validate()
      const isSavedFromCustomValid = defaultShiftForm.validateSavedFromCustom()
      const isBreaksValid = plannedBreakForms.every((form) =>
        form.validate({
          startAt: customShiftForm.startAt,
          endAt: customShiftForm.endAt,
        })
      )

      return isCommonValid && isCustomValid && isSavedFromCustomValid && isBreaksValid
    }
  }

  const scrollToErrors = () => {
    // Timeout to wait for the error to be rendered
    clearTimeout(scrollToErrorsTimeout.current)
    scrollToErrorsTimeout.current = setTimeout(() => {
      const errorAnchors = document.getElementsByClassName('error-anchor')
      const firstError = errorAnchors[0]
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 100)
  }

  const handleOnSubmitCustomShift = () => {
    if (defaultShiftForm.isSavedFromCustom) {
      onSubmitSaveShift()
    } else {
      onSubmitCustomShift({})
    }
  }

  const handleClose = () => {
    cleanPreview()
    onClose()
  }

  const onSubmit = () => {
    const isValid = handleErrors()
    if (!isValid) return scrollToErrors()
    const selectedShiftForms = defaultShiftForms.filter((form) => form.get('is_selected').value)
    isSavedShiftsTab ? onSubmitDefaultShifts(selectedShiftForms) : handleOnSubmitCustomShift()
    handleClose()
  }

  const selectedShiftsCount = defaultShiftForms.filter((f) => f.get('is_selected').value).length
  const shiftsCount = isSavedShiftsTab && !!selectedShiftsCount ? selectedShiftsCount : 1
  const shiftsToAddCount =
    commonSelectorsForm.employeeSelection?.ids?.length === 1
      ? shiftsCount
      : (commonSelectorsForm.employeeSelection?.ids?.length ?? 0)
  const submitting = submittingCustomShift || submittingDefaultShifts || submittingSavedShift

  const handleChangeTab = () => setTab('custom-shift')

  return (
    <Sidepanel
      open
      locked={submitting}
      title={i18n.t('shift_management.actions.add_shift')}
      footer={
        <>
          <Button onClick={handleClose} disabled={submitting} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={submitting} submit>
            {i18n.tp('shift_management.actions_sidepanel.add_shift', { count: shiftsToAddCount })}
          </Button>
        </>
      }
      onClose={handleClose}
      closeLabel="Close"
    >
      {loading ? (
        <Loading />
      ) : (
        <Box gap="s16">
          {isBackup && !hasSeenBackupShiftsCallout && (
            <Callout
              title={i18n.t('shift_management.backup_shifts.title')}
              description={i18n.t('shift_management.backup_shifts.creation')}
              type="info"
              onRemove={() => setHasSeenBackupShiftsCallout(true)}
            />
          )}
          <Tabs
            tabsAlign="center"
            onTabClicked={(tabId: TabsType) => setTab(tabId)}
            currentId={tab}
          >
            <Tabs.Tab
              id="saved-shifts"
              label={i18n.t('shift_management.actions_sidepanel.tabs.saved_shift')}
            >
              <Box paddingTop="s24" gap="s24">
                <SavedShiftsSection
                  forms={defaultShiftForms}
                  commonSelectorsForm={commonSelectorsForm}
                  defaultAttributes={defaultAttributesGraphQL}
                  selectableEmployeeIds={selectableEmployeeIds}
                  onToggleShift={(id) => {
                    showSavedShiftsError && setShowSavedShiftsError(false)
                    onToggleShift(id)
                  }}
                  handleChangeTab={handleChangeTab}
                  setTrackSearch={setTrackSearch}
                  showError={showSavedShiftsError}
                  leave={leave}
                />
              </Box>
            </Tabs.Tab>
            <Tabs.Tab
              id="custom-shift"
              label={i18n.t('shift_management.actions_sidepanel.tabs.custom_shift')}
            >
              <Box paddingTop="s24" gap="s24">
                <CustomShiftSection
                  form={customShiftForm}
                  defaultShiftForm={defaultShiftForm}
                  plannedBreakForms={plannedBreakForms}
                  onAddBreak={onAddBreak}
                  onDeleteBreak={onDeleteBreak}
                  commonSelectorsForm={commonSelectorsForm}
                  selectableEmployeeIds={selectableEmployeeIds}
                  leave={leave}
                />
              </Box>
            </Tabs.Tab>
          </Tabs>
        </Box>
      )}
    </Sidepanel>
  )
}

export default observer(AddShiftSidepanel)
